import React, { Fragment, type PropsWithChildren, type RefObject, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import { IconButton } from '@atlaskit/button/new';
import LegacyButton from '@atlaskit/button/standard-button';
import Heading from '@atlaskit/heading';
import EditorAddIcon from '@atlaskit/icon/core/migration/add--editor-add';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { SpotlightTarget } from '@atlaskit/onboarding';
import SectionMessage from '@atlaskit/section-message';
import Tooltip from '@atlaskit/tooltip';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { Flex } from '@atlassian/eoc-flex';
import { useResponderNames } from '@atlassian/eoc-responder-names';
import { routingRulesResource } from '@atlassian/eoc-routing-rules/resources';
import { useResource } from '@atlassian/eoc-rrr-helpers';
import { useMonitorRuleCards } from '@atlassian/eoc-rule-card';
import { Section } from '@atlassian/eoc-section';
import { ExperienceSuccess, useUFOComponentExperience } from '@atlassian/ufo';

import { SPOTLIGHT_NAMES } from '../../common/constants';
import { escalationPoliciesLoadExperience } from '../../common/constants/performance-metrics';
import { ChangeStateModal } from '../../common/ui/change-state-modal';
import { DeleteModal } from '../../common/ui/delete-modal';
import { EscalationPolicyModal } from '../../common/ui/escalation-policy-modal';
import { useTeamEscalationsController } from '../../controllers/team-escalations';

import { EscalationRules } from './escalation-rules';
import messages from './messages';
import { RuleCardContainer, SectionContent, TitleContainer } from './styled';

const FirstEntitySpotlight = ({ children }: PropsWithChildren<object>) => (
	<SpotlightTarget name={SPOTLIGHT_NAMES.firstEntity}>{children}</SpotlightTarget>
);

export default function EscalationPolicies() {
	useUFOComponentExperience(escalationPoliciesLoadExperience);
	const { data } = useResource(routingRulesResource);

	assert(
		!!data,
		new AssertionError(
			'EscalationPolicies',
			'data should be fetched if there is no loading and error',
		),
	);

	const { formatMessage } = useIntl();

	const {
		editingData,
		editingId,
		isChangeStateModalOpen,
		isCloning,
		isCreating,
		isDeleteModalOpen,
		isDeleting,
		isEscalationPolicyModalOpen,
		isUpdating,
		isPartiallyUpdating,
		closeDeleteModal,
		closeEscalationPolicyModal,
		closeChangeStateModal,

		handleChangeStateClick,
		handleChangeState,
		handleCollapseStatusChange,
		handleCloneClick,
		handleDeleteClick,
		handleDelete,
		handleEditClick,
		handleFormSubmit,
		handleOrderChange,
		openEscalationPolicyModal,
		toggleSection,
	} = useTeamEscalationsController();

	const {
		escalations: {
			collapsed,
			entities,
			availableNotifyTypes,
			hasEditRight,
			hasDeleteRight,
			hasRepeatRight,
		},
	} = data;

	const requestedResponders = entities
		.flatMap((entity) => entity.rules)
		.map((rule) => ({
			id: rule.notifyId,
			type: rule.type,
		}))
		.filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);

	const escalationNames = [...new Set(entities.map(({ name }) => name))];

	useResponderNames(requestedResponders);

	const isLimit = entities.length === 300;
	const isEmpty = entities.length === 0;

	useMonitorRuleCards({
		droppableId: 'escalation-policies',
		data: entities,
		onComplete: ({ updatedValue, targetIndex, sourceId }) =>
			handleOrderChange(updatedValue, targetIndex, sourceId),
	});

	const createPolicyButtonRef = useRef<HTMLButtonElement>(null);
	const createPolicyEmptyStateButtonRef = useRef<HTMLButtonElement>(null);

	const ruleActionButtonRefs = useRef<{
		[key: string]: RefObject<HTMLButtonElement> | null | undefined;
	}>({});

	const [returnFocusRef, setReturnFocusRef] = useState<RefObject<HTMLButtonElement> | undefined>(
		undefined,
	);

	return (
		<>
			<ExperienceSuccess experience={escalationPoliciesLoadExperience} />
			<Section
				isOpen={!collapsed}
				onClick={toggleSection}
				title={
					<TitleContainer>
						<Heading as="h2" size="medium">
							{formatMessage(messages.title)}
						</Heading>
						{data.escalations.entities.length > 0 && (
							<Badge testId="escalation-policies-entity-count-badge" max={300}>
								<span aria-hidden="true">{data.escalations.entities.length}</span>
							</Badge>
						)}
						{isLimit && (
							<Tooltip content={formatMessage(messages.maxPolicy)}>
								<Flex>
									<EditorPanelIcon
										color="currentColor"
										spacing="spacious"
										label={formatMessage(messages.maxPolicy)}
									/>
								</Flex>
							</Tooltip>
						)}
					</TitleContainer>
				}
				actions={
					hasEditRight ? (
						<Tooltip content={formatMessage(messages.add)}>
							<IconButton
								label={formatMessage(messages.add)}
								onClick={(e) => {
									setReturnFocusRef(createPolicyButtonRef);
									e.stopPropagation();
									openEscalationPolicyModal();
								}}
								isDisabled={isLimit}
								appearance="subtle"
								icon={EditorAddIcon}
								ref={createPolicyButtonRef}
							/>
						</Tooltip>
					) : null
				}
			>
				<SectionContent>
					{isEmpty && (
						<SectionMessage
							// SectionMessageAction doesn't accept a ref prop. Therefore, we use a button directly.
							actions={[
								<LegacyButton
									// TODO: (from codemod) "link" and "subtle-link" appearances are only available in LinkButton, please either provide a href prop then migrate to LinkButton, or remove the appearance from the default button.
									// https://product-fabric.atlassian.net/browse/DSP-19033
									appearance="link"
									spacing="none"
									onClick={() => {
										setReturnFocusRef(createPolicyEmptyStateButtonRef);
										return openEscalationPolicyModal();
									}}
									ref={createPolicyEmptyStateButtonRef}
								>
									{formatMessage(messages.emptyAdd)}
								</LegacyButton>,
							]}
						>
							{formatMessage(messages.empty)}
						</SectionMessage>
					)}
					{!isEmpty &&
						entities.map((entity, i) => {
							const Wrapper = i === 0 ? FirstEntitySpotlight : Fragment;

							return (
								<Wrapper key={entity.id}>
									<RuleCardContainer>
										<EscalationRules
											entity={entity}
											shouldShowSpinner={
												editingId === entity.id && (isPartiallyUpdating || isDeleting)
											}
											hasDeleteRight={hasDeleteRight}
											hasEditRight={hasEditRight}
											onClone={() => {
												setReturnFocusRef(ruleActionButtonRefs?.current[entity.id] || undefined);
												return handleCloneClick(entity);
											}}
											onDelete={() => {
												setReturnFocusRef(ruleActionButtonRefs?.current[entity.id] || undefined);
												return handleDeleteClick(entity);
											}}
											onEdit={() => {
												setReturnFocusRef(ruleActionButtonRefs?.current[entity.id] || undefined);
												return handleEditClick(entity);
											}}
											onStateChange={() => {
												setReturnFocusRef(ruleActionButtonRefs?.current[entity.id] || undefined);
												handleChangeStateClick(entity);
											}}
											onUpdateCollapseStatus={() => handleCollapseStatusChange(entity)}
											ref={(el) => {
												ruleActionButtonRefs.current[entity.id] = el?.getActionButtonRef();
											}}
										/>
									</RuleCardContainer>
								</Wrapper>
							);
						})}
				</SectionContent>
			</Section>
			<EscalationPolicyModal
				isOpen={isEscalationPolicyModalOpen}
				onClose={closeEscalationPolicyModal}
				notifyTypes={availableNotifyTypes}
				escalation={editingData}
				isCloning={isCloning}
				escalationNames={escalationNames}
				onSubmit={handleFormSubmit}
				isLoading={isUpdating || isCreating}
				hasRepeatRight={hasRepeatRight}
				returnFocusRef={returnFocusRef}
			/>
			<DeleteModal
				isOpen={isDeleteModalOpen}
				onClose={closeDeleteModal}
				onConfirm={handleDelete}
				name={editingData?.name || ''}
				returnFocusRef={returnFocusRef}
			/>
			<ChangeStateModal
				isOpen={isChangeStateModalOpen}
				onClose={closeChangeStateModal}
				onConfirm={handleChangeState}
				name={editingData?.name || ''}
				isEnabled={editingData?.enabled || false}
				returnFocusRef={returnFocusRef}
			/>
		</>
	);
}
